import { useState } from 'react';
import { useParams } from 'react-router';
import { ConnectedProps, connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Dialog,
  Button,
  Loader,
  Spinner,
  Table,
  TChangeEvent,
  ITableProps,
} from '@oliasoft-open-source/react-ui-library';
import { addConceptDesigns } from 'store/entities/campaigns/campaigns';
import translations from 'src/internationalisation/translation-map.json';
import { IDesignItem } from 'src/common/interfaces/campaigns.interfaces';
import { selectSubjectId } from 'src/store/entities/authorization/selectors';
import {
  selectCampaignDesigns,
  selectIsAddingCampaigns,
  selectIsFetchingCampaign,
} from 'src/store/entities/campaigns/selectors';
import type { TRootState } from 'src/store/store-types';
import { getFilteredData, getHeaderFilter, getHeaderLabels, getHeaderRow, getRows } from '../utils/utils';

type THierarchyDesignTableModal = PropsFromRedux & {
  setModalVisible: (param: boolean) => void;
  designs: IDesignItem[];
  isFetching: boolean;
  isAdding: boolean;
  name: string;
  campaignId: string;
  subjectId: string;
};

const HierarchyTableModal = ({
  setModalVisible,
  designs,
  isFetching,
  isAdding,
  addConceptDesigns,
  name,
  campaignId,
  subjectId,
}: THierarchyDesignTableModal) => {
  const { t } = useTranslation();
  const { company } = useParams();

  const [filters, setFilters] = useState<{ [key: string]: string }>({});
  const [selectedPage, setSelectedPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [designsValue, setDesignsValue] = useState<IDesignItem[]>([]);

  const onClose = () => setModalVisible(false);
  const onSubmit = () => {
    addConceptDesigns({
      companyId: company,
      name,
      designs: designsValue,
      campaignId,
      subjectId,
    });
    setModalVisible(false);
  };
  const headerLabels = getHeaderLabels(t, translations);
  const headerRow = getHeaderRow(headerLabels);
  const headerFilter = getHeaderFilter(headerLabels, filters, setFilters, t, translations, true, setSelectedPage);

  const startIndex = (selectedPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const filteredData = getFilteredData(designs, filters);

  const rows = getRows(filteredData, designsValue, setDesignsValue);

  const filteredRows = rows.slice(startIndex, endIndex);

  const table: ITableProps['table'] = {
    testId: 'choose-estimates-or-designs-modal-table',
    fixedWidth: '100%',
    headers: [{ cells: [{}, ...headerRow] }, { cells: [{}, ...headerFilter] }],
    rows: filteredRows,
    footer: {
      pagination: {
        rowCount: rows.length,
        selectedPage,
        rowsPerPage: {
          onChange: (evt: TChangeEvent) => setRowsPerPage(+evt.target.value),
          options: [
            { label: `10 / ${t(translations.page)}`, value: 10 },
            { label: `20 / ${t(translations.page)}`, value: 20 },
            { label: `50 / ${t(translations.page)}`, value: 50 },
          ],
          value: rowsPerPage,
        },
        onSelectPage: setSelectedPage,
        small: true,
      },
    },
  };

  return (
    <Modal visible centered onEscape={onClose}>
      <Dialog
        dialog={{
          heading: t(translations.campaigns_chooseEstimatesDesigns),
          width: 'auto',
          content: (
            <div
              style={{
                position: 'relative',
                margin: 'calc(var(--padding) * -1)',
              }}
            >
              {isFetching && (
                <Loader text={t(translations.fetching)} theme="light" cover>
                  <Spinner dark />
                </Loader>
              )}
              <form style={{ padding: 'var(--padding)' }}>
                <Table table={table} />
              </form>
            </div>
          ),
          footer: (
            <>
              <Button
                label={
                  designsValue?.length > 0
                    ? `${t(translations.confirmSelection)} (${designsValue?.length})`
                    : t(translations.confirmSelection)
                }
                testId="choose-estimates-or-designs-modal-button-confirm"
                colored
                onClick={onSubmit}
                disabled={isAdding}
              />
              <Button label={t(translations.cancel)} onClick={onClose} disabled={isAdding} />
            </>
          ),
          onClose,
        }}
      />
    </Modal>
  );
};

const mapStateToProps = ({ entities }: TRootState) => ({
  subjectId: selectSubjectId(entities),
  designs: selectCampaignDesigns(entities),
  isFetching: selectIsFetchingCampaign(entities),
  isAdding: selectIsAddingCampaigns(entities),
});

const mapDispatchToProps = {
  addConceptDesigns,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = connector(HierarchyTableModal);

export { Container as HierarchyTableModal };
